// Auto-renew on a sold membership (memberships.setAutoRenew, F-27). On: the plan's renewal terms
// (memberships.renewalTerms) are shown word for word and sent back with the customer's yes, exactly as
// a sale records it; never a bare switch. Off: confirmed, then recorded. The membership sheet takes
// the server's renewal as it comes back, so "Renews to" (the card) follows without a re-read.

import { useEffect, useState } from 'react';
import { Link } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { FormActs, TermsAgreed } from './member-parts.tsx';
import { autoRenewSaid, offOutcome, type RenewalWithCopy } from './auto-renew-model.ts';
import { renewalConsent, type MembershipDetail, type RenewalTerms } from './model.ts';

export function AutoRenew({
  detail: d,
  membership,
  onChanged,
}: {
  detail: MembershipDetail;
  /** Whose it is, for the button's name: "Fairview residence’s Comfort Club membership". */
  membership: string;
  onChanged: (next: RenewalWithCopy) => void;
}) {
  const on = !!d.renewal?.on;
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState<RenewalTerms | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    if (!open || on || terms) return;
    let current = true;
    void client
      .call<RenewalTerms>('memberships.renewalTerms', { typeId: d.typeId })
      .then((t) => current && setTerms(t))
      .catch((e: unknown) => current && setTermsError(errorText(e, 'Could not load the renewal terms.')));
    return () => {
      current = false;
    };
  }, [open, on, terms, d.typeId]);
  const consent = renewalConsent(!on, terms, agreed);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ renewal: RenewalWithCopy }>('memberships.setAutoRenew', {
        membershipId: d.id,
        on: !on,
        ...(consent.consent && !on ? { renewalConsent: consent.consent } : {}),
      });
      setSaid(autoRenewSaid(!on, r.renewal));
      setOpen(false);
      setAgreed(false);
      onChanged(r.renewal);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bp-member-renew">
      {said && (
        <p className="bp-member-line" role="status">
          {said}
        </p>
      )}
      {!open ? (
        <span>
          <Link
            label={`${on ? 'Turn auto-renew off' : 'Turn auto-renew on'} for ${membership}…`}
            onPress={() => {
              setSaid(null);
              setOpen(true);
            }}
          >
            {on ? 'Turn auto-renew off…' : 'Turn auto-renew on…'}
          </Link>
        </span>
      ) : on ? (
        <div className="bp-member-terms" role="group" aria-label="Turn auto-renew off">
          <p className="bp-member-terms-text">{offOutcome}</p>
          <FormActs
            back={() => setOpen(false)}
            backLabel="Keep it on"
            busy={busy}
            error={error}
            label={busy ? 'Turning it off…' : 'Turn auto-renew off'}
            onSave={() => void save()}
          />
        </div>
      ) : (
        <TermsAgreed terms={terms} error={termsError} agreed={agreed} onAgree={setAgreed}>
          <FormActs
            back={() => setOpen(false)}
            backLabel="Cancel"
            busy={busy}
            error={error}
            label={busy ? 'Turning it on…' : 'Turn auto-renew on'}
            isDisabled={!consent.ready || !terms}
            onSave={() => void save()}
          />
        </TermsAgreed>
      )}
    </div>
  );
}
